import { useEffect, useState, useCallback, useRef } from "react";
import { usDate } from "@/lib/dates";
import { viewFromApi } from "@/components/FileViewer";
import { FilePlus2, Loader2, Paperclip, X, CheckCircle2, XCircle, Clock, Eye } from "lucide-react";
import { toast } from "sonner";
import parentApi from "@/lib/parentApi";
import { formatApiErrorDetail } from "@/lib/api";

const MAX_BYTES = 6 * 1024 * 1024;
const STATUS = {
  pending: { label: "Awaiting review", icon: Clock, cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved", icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800" },
  declined: { label: "Declined", icon: XCircle, cls: "bg-rose-100 text-rose-800" },
};

export const openParentFile = (url) => viewFromApi(parentApi, url, { title: "Excuse note attachment" });

export function ExcuseRequests({ studentId }) {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ date: "", end_date: "", reason: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => parentApi.get(`/parent/students/${studentId}/excuse-requests`).then(({ data }) => setList(data)).catch(() => setList([])), [studentId]);
  useEffect(() => { setList(null); setOpen(false); load(); }, [load]);

  const pickFile = (e) => {
    const fl = e.target.files?.[0]; if (!fl) return;
    if (!(fl.type === "application/pdf" || fl.type.startsWith("image/"))) { toast.error("Attach a photo or a PDF"); return; }
    if (fl.size > MAX_BYTES) { toast.error("File too large — 6MB max"); return; }
    const r = new FileReader();
    r.onload = () => setFile({ name: fl.name, data_url: r.result });
    r.readAsDataURL(fl);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.date) { toast.error("Pick the absence date"); return; }
    if (f.reason.trim().length < 3) { toast.error("Tell the school why your child was absent"); return; }
    setBusy(true);
    try {
      await parentApi.post(`/parent/students/${studentId}/excuse-requests`, {
        date: f.date, end_date: f.end_date || undefined, reason: f.reason.trim(),
        doc_data_url: file?.data_url, doc_filename: file?.name,
      });
      toast.success("Excuse note sent to the school");
      setOpen(false); setF({ date: "", end_date: "", reason: "" }); setFile(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send"); }
    finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl bg-white shadow-sm p-4" data-testid="parent-excuse-section">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Excuse notes</div>
        {!open && (
          <button onClick={() => setOpen(true)} data-testid="parent-excuse-new" className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-white px-3 py-1.5 text-xs font-semibold hover:shadow transition">
            <FilePlus2 className="h-3.5 w-3.5" /> Submit excuse note
          </button>
        )}
      </div>

      {open && (
        <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3 mb-3" data-testid="parent-excuse-form">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-600">Absent on *
              <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="parent-excuse-date" className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm outline-none focus:border-primary" />
            </label>
            <label className="text-xs text-slate-600">Until (optional)
              <input type="date" value={f.end_date} min={f.date || undefined} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="parent-excuse-end" className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm outline-none focus:border-primary" />
            </label>
          </div>
          <label className="block text-xs text-slate-600">Reason *
            <textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} rows={3} placeholder="e.g. Doctor's appointment — note attached" data-testid="parent-excuse-reason" className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary" />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={pickFile} className="hidden" data-testid="parent-excuse-file" />
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="parent-excuse-attach" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100">
              <Paperclip className="h-3.5 w-3.5" /> {file ? "Change file" : "Attach photo or PDF"}
            </button>
            {file && <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 px-2.5 py-1 text-xs" data-testid="parent-excuse-file-chip">{file.name}<button type="button" onClick={() => setFile(null)}><X className="h-3 w-3" /></button></span>}
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={busy} data-testid="parent-excuse-submit" className="flex-1 h-10 rounded-lg bg-primary text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus2 className="h-4 w-4" />} Send to school</button>
            <button type="button" onClick={() => setOpen(false)} data-testid="parent-excuse-cancel" className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm text-slate-600">Cancel</button>
          </div>
        </form>
      )}

      {list === null && <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />}
      {list?.length === 0 && !open && <div className="text-sm text-slate-400" data-testid="parent-excuse-empty">No excuse notes yet. Absent day? Send the school a note here.</div>}
      <ul className="divide-y divide-slate-100">
        {list?.map((r) => { const s = STATUS[r.status]; const I = s.icon; return (
          <li key={r.id} className="py-2.5 flex items-start gap-3 text-sm" data-testid={`parent-excuse-${r.id}`}>
            <span className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${s.cls}`} data-testid={`parent-excuse-status-${r.id}`}><I className="h-3 w-3" /> {s.label}</span>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-slate-900">{usDate(r.date)}{r.end_date ? ` → ${usDate(r.end_date)}` : ""}</div>
              <div className="text-xs text-slate-600">{r.reason}</div>
              {r.review_note && <div className="text-xs text-slate-500 italic mt-0.5">School: “{r.review_note}”</div>}
            </div>
            {r.has_doc && <button onClick={() => openParentFile(`/parent/excuse-requests/${r.id}/file`)} data-testid={`parent-excuse-file-${r.id}`} title="View attachment" className="h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 inline-flex items-center justify-center"><Eye className="h-4 w-4" /></button>}
          </li>
        ); })}
      </ul>
    </section>
  );
}
